import { useCallback, useState } from "react";

import { useMedplum } from "@medplum/react";

import { Button, Drawer, Group, Stack, TextInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { useAppDispatch } from "@/store";
import { clientFlagsChanged } from "@/store/slices/client-flags";
import { getActiveOrganizationId } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

import {
  ACTIVE_FLAGS_HEADING,
  ADD_A_FLAG_HEADING,
  CLIENT_FLAG_REMOVED_MESSAGE,
  CLIENT_FLAG_SEARCH_PLACEHOLDER,
  CLIENT_FLAGS_SAVED_MESSAGE,
  clientFlagsDrawerTitle,
  NO_ACTIVE_CLIENT_FLAGS_MESSAGE,
  NO_LIBRARY_FLAGS_LEFT_MESSAGE,
  NO_MATCHING_CLIENT_FLAGS_MESSAGE,
  SAVE_CLIENT_FLAGS_LABEL,
} from "../constants/client-flags";
import {
  APPLY_CLIENT_FLAGS_FAILURE_MESSAGES,
  GET_PRACTICE_FLAGS_FAILURE_MESSAGES,
  REMOVE_CLIENT_FLAG_FAILURE_MESSAGES,
} from "../constants/failure-messages";
import { NO_PRACTICE_FLAGS } from "../constants/flags";
import { DRAWER_LOADING_HEIGHT, DRAWER_WIDTH, SUCCESS_TOAST_DURATION } from "../constants/ui";
import useClientFlags from "../hooks/useClientFlags";
import useClientFlagStaging from "../hooks/useClientFlagStaging";
import useFlagRead from "../hooks/useFlagRead";
import { ClientFlag, ClientFlagsDrawerProps } from "../interfaces/client-flags";
import { removeClientFlag } from "../services/client-flag-removal";
import { applyClientFlags } from "../services/client-flags";
import { getPracticeFlags } from "../services/flags";
import { buildFlagReadScope } from "../utils/flag-read-scope";

import ClientFlagDrawerRow from "./ClientFlagDrawerRow";
import ClientFlagVisibilityInfo from "./ClientFlagVisibilityInfo";
import FlagDrawerHeader from "./FlagDrawerHeader";
import RemoveClientFlagStep from "./RemoveClientFlagStep";

/**
 * Lists the flags on a chart and the library entries still available to add.
 *
 * Adds are staged locally and written only on `Save`, so closing with `X` discards them. A removal
 * is the other way round: `−` swaps the drawer to its Remove Flag step, which captures why and
 * writes on `Remove`.
 *
 * A viewer whose role cannot write a Flag gets the active list alone: no `−`, no `Add a flag`
 * section, and no `Save`.
 */
export default function ClientFlagsDrawer({
  opened,
  clientId,
  clientFirstName,
  onClose,
  onSaved,
}: ClientFlagsDrawerProps) {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const { hasFlagWritePermission } = usePractitionerPermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, { open: startSubmitting, close: stopSubmitting }] = useDisclosure(false);
  /** The row the Remove Flag step is acting on; the drawer shows that step while it is set. */
  const [flagToRemove, setFlagToRemove] = useState<ClientFlag | null>(null);
  /**
   * Flags whose removal is still writing, by id. The step can move to another flag while one is in
   * flight, so each step reads only its own flag's state.
   */
  const [removingFlagIds, setRemovingFlagIds] = useState<ReadonlySet<string>>(() => new Set());

  const practiceId = getActiveOrganizationId();
  // Both reads wait for the drawer to open: it is mounted once per client row on the client list,
  // so reading on mount would turn a page of clients into a burst of chart and library lookups.
  const {
    flags: appliedFlags,
    isLoading: isLoadingChart,
    errorMessage,
  } = useClientFlags(clientId, opened);

  const readLibrary = useCallback(
    () => getPracticeFlags({ medplum, practiceId }),
    [medplum, practiceId],
  );

  const { data: libraryData, isLoading: isLoadingLibrary } = useFlagRead({
    read: readLibrary,
    // The library feeds only `Add a flag`, so a viewer who cannot add never reads it.
    enabled: opened && hasFlagWritePermission,
    scope: buildFlagReadScope(practiceId),
    failureMessages: GET_PRACTICE_FLAGS_FAILURE_MESSAGES,
  });

  const libraryFlags = libraryData ?? NO_PRACTICE_FLAGS;

  const { activeFlags, availableFlags, stagedFlagIds, hasStagedChanges, stage, unstage, reset } =
    useClientFlagStaging(appliedFlags, libraryFlags, searchTerm, opened);

  const isLoading = isLoadingChart || (hasFlagWritePermission && isLoadingLibrary);

  const handleClose = () => {
    reset();
    setSearchTerm("");
    setFlagToRemove(null);
    onClose();
  };

  /**
   * A removal is written on `Remove` rather than staged. Staging exists for adds because a
   * half-built list should be discardable; taking a flag off a chart is one deliberate act, and the
   * row leaving the list is its own confirmation.
   */
  const handleRemove = async (reason: string) => {
    if (!flagToRemove || removingFlagIds.has(flagToRemove.id)) return;

    const removedFlag = flagToRemove;
    setRemovingFlagIds((current) => new Set(current).add(removedFlag.id));

    const result = await removeClientFlag({
      medplum,
      clientId,
      flagId: removedFlag.id,
      reason,
    });

    setRemovingFlagIds((current) => {
      const remaining = new Set(current);
      remaining.delete(removedFlag.id);
      return remaining;
    });

    if (!result.ok) {
      // Named, because the step on screen may already belong to another flag.
      toast({
        type: "error",
        title: `Failed to remove ${removedFlag.name}`,
        message: REMOVE_CLIENT_FLAG_FAILURE_MESSAGES[result.reason],
        autoClose: false,
      });

      return;
    }

    toast({
      type: "success",
      title: "Flag removed",
      message: CLIENT_FLAG_REMOVED_MESSAGE(removedFlag.name),
      autoClose: SUCCESS_TOAST_DURATION,
    });

    setFlagToRemove((current) => (current?.id === removedFlag.id ? null : current));
    // Every surface reading this chart's flags — this drawer's list, the info box, the Flag
    // Archive — reads again on this signal.
    dispatch(clientFlagsChanged(clientId));
    onSaved?.();
  };

  const handleSave = async () => {
    startSubmitting();

    const result = await applyClientFlags({
      medplum,
      clientId,
      practiceId,
      libraryFlagIds: stagedFlagIds,
    });

    stopSubmitting();

    if (!result.ok) {
      toast({
        type: "error",
        title: "Failed to add flags",
        message: APPLY_CLIENT_FLAGS_FAILURE_MESSAGES[result.reason],
        autoClose: false,
      });

      return;
    }

    toast({
      type: "success",
      title: "Flags added",
      message: CLIENT_FLAGS_SAVED_MESSAGE,
      autoClose: SUCCESS_TOAST_DURATION,
    });
    dispatch(clientFlagsChanged(clientId));
    onSaved?.();
    handleClose();
  };

  return (
    <Drawer.Root opened={opened} position="right" size={DRAWER_WIDTH} onClose={handleClose}>
      <Drawer.Overlay />
      <Drawer.Content data-testid="client-flags-drawer">
        {flagToRemove ? (
          <RemoveClientFlagStep
            flag={flagToRemove}
            key={flagToRemove.id}
            onRemove={handleRemove}
            onBack={() => setFlagToRemove(null)}
            isRemoving={removingFlagIds.has(flagToRemove.id)}
          />
        ) : (
          <>
            <FlagDrawerHeader onClose={handleClose} title={clientFlagsDrawerTitle(clientFirstName)}>
              {hasFlagWritePermission && (
                <Button
                  color="burgundy.9"
                  onClick={handleSave}
                  loading={isSubmitting}
                  disabled={!hasStagedChanges}
                  data-testid="client-flags-save"
                >
                  {SAVE_CLIENT_FLAGS_LABEL}
                </Button>
              )}
            </FlagDrawerHeader>
            <Drawer.Body p="lg">
              <Stack gap="xl">
                {isLoading && <Loading height={DRAWER_LOADING_HEIGHT} />}
                {!isLoading && errorMessage && <TextWrapper c="red.7">{errorMessage}</TextWrapper>}
                {!isLoading && !errorMessage && (
                  <>
                    <Stack gap="md">
                      <Group gap="xs">
                        <TextWrapper variant="bold">
                          {ACTIVE_FLAGS_HEADING(activeFlags.length)}
                        </TextWrapper>
                        <ClientFlagVisibilityInfo />
                      </Group>
                      {activeFlags.length ? (
                        activeFlags.map((active) =>
                          active.state === "applied" ? (
                            <ClientFlagDrawerRow
                              variant="applied"
                              flag={active.flag}
                              key={active.flag.id}
                              onRemove={hasFlagWritePermission ? setFlagToRemove : undefined}
                            />
                          ) : (
                            <ClientFlagDrawerRow
                              variant="staged"
                              flag={active.flag}
                              onUnstage={unstage}
                              key={active.flag.id}
                            />
                          ),
                        )
                      ) : (
                        <TextWrapper size="sm" c="gray.7">
                          {NO_ACTIVE_CLIENT_FLAGS_MESSAGE}
                        </TextWrapper>
                      )}
                    </Stack>

                    {hasFlagWritePermission && (
                      <Stack gap="md">
                        <TextWrapper variant="bold">{ADD_A_FLAG_HEADING}</TextWrapper>
                        <TextInput
                          value={searchTerm}
                          data-testid="client-flags-search"
                          placeholder={CLIENT_FLAG_SEARCH_PLACEHOLDER}
                          onChange={(event) => setSearchTerm(event.currentTarget.value)}
                        />
                        {availableFlags.map((flag) => (
                          <ClientFlagDrawerRow
                            flag={flag}
                            key={flag.id}
                            variant="add"
                            onAdd={stage}
                          />
                        ))}
                        {!availableFlags.length && (
                          <TextWrapper size="sm" c="gray.7">
                            {searchTerm.trim()
                              ? NO_MATCHING_CLIENT_FLAGS_MESSAGE
                              : NO_LIBRARY_FLAGS_LEFT_MESSAGE}
                          </TextWrapper>
                        )}
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </Drawer.Body>
          </>
        )}
      </Drawer.Content>
    </Drawer.Root>
  );
}
